import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Input,
  FormControl,
  FormLabel,
  Textarea,
  Select,
  Center,
  Spinner,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  IconButton,
  useMediaQuery,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Stack,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogCloseButton,
} from '@chakra-ui/react';
import { AddIcon, ChevronUpIcon, ChevronDownIcon } from '@chakra-ui/icons';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import CaseDetailsModal from '../components/CaseDetailsModal';
import { useSpeciesField } from '../hooks/useSpeciesField';

const Cases = ({ userRole, isAuthenticated, onNotAuthenticated }) => {
  const [cases, setCases] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sorting, setSorting] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  // eslint-disable-next-line no-unused-vars
  const [isLoadingForm, setIsLoadingForm] = useState(false);
  const toast = useToast();
  const [isMobile] = useMediaQuery('(max-width: 768px)');
  
  // Get species field utilities from hook
  const { speciesOptions, SPECIES_LIST } = useSpeciesField();

  // Check authentication on mount and redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      onNotAuthenticated?.();
    }
  }, [isAuthenticated, onNotAuthenticated]);

  // Get today's date in YYYY-MM-DD format for date input
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Memoize the current year to prevent re-renders on every keystroke
  const currentYear = useMemo(() => new Date().getFullYear().toString().slice(-2), []);

  const [formData, setFormData] = useState({
    title: '',
    wrmdPrefix: 'PWSD',
    wrmd: '',
    location: '',
    status: 'Candidate',
    description: '',
    pointOfContact: '',
    date: getTodayDate(),
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [selectedCase, setSelectedCase] = useState(null);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isDetailsOpen, onOpen: onDetailsOpen, onClose: onDetailsClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [caseToDelete, setCaseToDelete] = useState(null);

  // Fetch cases from backend API when component mounts
  useEffect(() => {
    fetchCases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCases = async () => {
    console.log('=== FETCH CASES START ===');
    setIsLoading(true);
    try {
      // Get base API URL from environment
      const baseApiUrl = process.env.REACT_APP_BASE_API_URL || 'http://localhost:8000';
      const apiUrl = `${baseApiUrl}/cases.php`;

      console.log('Fetching cases from:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      console.log('Fetch cases response status:', response.status);
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Not authenticated - please sign in first');
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error ${response.status}`);
      }

      const data = await response.json();
      console.log('Cases data received:', data);
      console.log('Number of cases:', data.cases ? data.cases.length : 0);
      
      if (data.cases) {
        // Sort cases by date (newest first)
        const sortedCases = data.cases.sort((a, b) => {
          const dateA = new Date(a.date || 0);
          const dateB = new Date(b.date || 0);
          return dateB - dateA;
        });
        setCases(sortedCases);
      } else if (data.error) {
        throw new Error(data.error);
      }
    } catch (error) {
      console.error('Error fetching cases:', error);
      
      // Check if this is an authentication error
      const isAuthError = error.message && error.message.includes('Not authenticated');
      
      toast({
        title: 'Error',
        description: error.message || 'Failed to load cases',
        status: 'error',
        duration: isAuthError ? null : 4000, // null = infinite for auth errors
        isClosable: !isAuthError, // Can't close auth error toast
        position: 'top-right',
        render: isAuthError ? ({ onClose }) => (
          <Box bg="red.500" px={4} py={3} rounded="md" color="white">
            <Text fontWeight="bold" mb={2}>{error.message}</Text>
            <Button
              size="sm"
              bg="red.600"
              _hover={{ bg: 'red.700' }}
              onClick={() => {
                onClose?.();
                onNotAuthenticated?.();
              }}
            >
              Go to Login
            </Button>
          </Box>
        ) : undefined,
      });
      // Set empty cases array to show "No cases found"
      setCases([]);
    } finally {
      setIsLoading(false);
      console.log('=== FETCH CASES END ===');
    }
  };

  const handleAddCase = () => {
    onOpen();
  };

  const handleViewDetails = (caseItem) => {
    setSelectedCase(caseItem);
    onDetailsOpen();
  };

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleSubmit = async () => {
    console.log('=== CREATE CASE SUBMIT START ===');
    console.log('Form data:', formData);
    
    // Validate required fields
    const errors = {};
    if (!formData.title || formData.title.trim() === '') {
      errors.title = 'Species is required';
    }
    if (!formData.wrmd || formData.wrmd.trim() === '') {
      errors.wrmd = 'WMRD Code is required';
    }
    if (!formData.location || formData.location.trim() === '') {
      errors.location = 'General Region is required';
    }

    console.log('Validation errors:', errors);

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      console.log('Validation failed - stopping submission');
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Clear validation errors if all fields are valid
    setValidationErrors({});

    setIsLoadingForm(true);
    try {
      const baseApiUrl = process.env.REACT_APP_BASE_API_URL || 'http://localhost:8000';
      console.log('Base API URL:', baseApiUrl);
      
      // Prepare data for backend - convert date if needed
      const dataToSave = { ...formData };
      
      // Combine WRMD prefix and code (e.g., "PWSD 25-1455")
      if (dataToSave.wrmdPrefix && dataToSave.wrmd) {
        const yearShort = new Date().getFullYear().toString().slice(-2);
        dataToSave.case_number = `${dataToSave.wrmdPrefix} ${yearShort}-${dataToSave.wrmd}`;
      } else if (dataToSave.wrmd) {
        dataToSave.case_number = dataToSave.wrmd;
      }
      
      console.log('Case number being set to:', dataToSave.case_number);
      
      // If date is in YYYY-MM-DD format, convert to MM/DD/YY for backend
      if (dataToSave.date && dataToSave.date.includes('-')) {
        const [year, month, day] = dataToSave.date.split('-');
        const yearShort = year.slice(-2);
        dataToSave.date = `${month}/${day}/${yearShort}`;
        console.log('Converted date from ISO to MM/DD/YY:', dataToSave.date);
      }
      
      console.log('Data to save (before fetch):', dataToSave);
      
      const response = await fetch(`${baseApiUrl}/cases.php`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataToSave),
      });

      console.log('Fetch response received - Status:', response.status);
      const responseData = await response.json();
      console.log('Response JSON data:', responseData);

      if (!response.ok) {
        console.error('Response not OK - throwing error');
        throw new Error(responseData.error || `HTTP error ${response.status}`);
      }

      console.log('Response successful - refetching cases');
      // Refetch cases to get the newly created case from the backend
      await fetchCases();
      
      // Reset form with defaults
      setFormData({
        title: '',
        wrmdPrefix: 'PWSD',
        wrmd: '',
        location: '',
        status: 'Candidate',
        description: '',
        pointOfContact: '',
        date: getTodayDate(),
      });
      
      console.log('Form reset - showing success toast');
      toast({
        title: 'Success',
        description: 'Case created successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      onClose();
      console.log('=== CREATE CASE SUBMIT SUCCESS ===');
    } catch (error) {
      console.error('=== CREATE CASE ERROR ===');
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create case',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsLoadingForm(false);
      console.log('=== CREATE CASE SUBMIT END (finally) ===');
    }
  };

  const handleModalClose = () => {
    setValidationErrors({});
    onClose();
  };

  const handleDeleteClick = (caseItem) => {
    setCaseToDelete(caseItem);
    onDeleteOpen();
  };

  const handleConfirmDelete = async () => {
    try {
      const baseApiUrl = process.env.REACT_APP_BASE_API_URL || 'http://localhost:8000';
      const apiUrl = `${baseApiUrl}/cases.php`;

      const response = await fetch(apiUrl, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: caseToDelete.id }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error ${response.status}`);
      }

      // Remove case from local state instead of refetching to avoid screen flash
      setCases(cases.filter(c => c.id !== caseToDelete.id));

      toast({
        title: 'Success',
        description: 'Case deleted successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      onDeleteClose();
      setCaseToDelete(null);
    } catch (error) {
      console.error('Error deleting case:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete case',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const getStatusColor = useCallback((status) => {
    switch (status) {
      case 'Candidate':
        return 'blue';
      case 'Candidate: Wild Foster':
        return 'cyan';
      case 'Investigation':
        return 'purple';
      case 'R&R in Process':
        return 'orange';
      case 'Renest Successful':
        return 'green';
      case 'Foster Successful':
        return 'teal';
      case 'Renest/Foster Unsuccessful':
        return 'red';
      case 'Patient HE/DIC':
        return 'pink';
      case 'Not a Candidate':
        return 'gray';
      default:
        return 'gray';
    }
  }, []);

  // Calculate pagination
  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  // Mobile Card Component
  const MobileCaseCard = ({ caseItem }) => (
    <Card mb={4} borderLeft="4px" borderLeftColor="blue.700">
      <CardHeader py={2}>
        <HStack justify="space-between" align="center">
          <VStack align="start" spacing={0} flex={1}>
            <Text fontSize="sm" fontWeight="bold" color="blue.800">
              {caseItem.id}
            </Text>
            <Text fontSize="md" fontWeight="bold">
              {caseItem.title}
            </Text>
          </VStack>
          <Badge colorScheme={getStatusColor(caseItem.status)}>
            {caseItem.status}
          </Badge>
        </HStack>
      </CardHeader>
      <Divider />
      <CardBody>
        <Stack spacing={3}>
          <Box>
            <Text fontSize="xs" color="gray.700" fontWeight="semibold">
              LOCATION
            </Text>
            <Text fontSize="sm">{caseItem.location}</Text>
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.700" fontWeight="semibold">
              DATE ADMITTED
            </Text>
            <Text fontSize="sm">{caseItem.date}</Text>
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.700" fontWeight="semibold">
              POINT OF CONTACT
            </Text>
            <Text fontSize="sm">{caseItem.pointOfContact || 'N/A'}</Text>
          </Box>
          {caseItem.description && (
            <Box>
              <Text fontSize="xs" color="gray.700" fontWeight="semibold">
                DESCRIPTION
              </Text>
              <Text fontSize="sm" noOfLines={2}>
                {caseItem.description}
              </Text>
            </Box>
          )}
          <Button
            size="sm"
            bg="blue.700"
            color="white"
            _hover={{ bg: "blue.800" }}
            variant="outline"
            width="full"
            onClick={() => handleViewDetails(caseItem)}
          >
            View Details
          </Button>
        </Stack>
      </CardBody>
    </Card>
  );

  // Define columns for TanStack Table
  const columns = useMemo(() => [
    {
      accessorKey: 'case_number',
      header: 'Case Number',
      cell: (info) => <Text fontSize="sm" fontWeight="600">{info.getValue()}</Text>,
    },
    {
      accessorKey: 'title',
      header: 'Band Code',
      cell: (info) => <Text fontWeight="medium">{info.getValue()}</Text>,
    },
    {
      accessorKey: 'location',
      header: 'Location',
    },
    {
      accessorKey: 'date',
      header: 'Date Admitted',
    },
    {
      accessorKey: 'pointOfContact',
      header: 'Volunteer',
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: (info) => (
        <Badge colorScheme={getStatusColor(info.getValue())}>
          {info.getValue()}
        </Badge>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: (info) => (
        <HStack spacing={2}>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => handleViewDetails(info.row.original)}
          >
            View
          </Button>
          {userRole === 'admin' && (
            <Button
              size="sm"
              variant="ghost"
              colorScheme="red"
              onClick={() => handleDeleteClick(info.row.original)}
            >
              Delete
            </Button>
          )}
        </HStack>
      ),
    },
  ], [userRole]);

  // Initialize table with ALL cases for sorting, then paginate
  const table = useReactTable({
    data: cases,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Get sorted rows and paginate them
  const sortedRows = table.getRowModel().rows;
  const totalItems = sortedRows.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRows = sortedRows.slice(startIndex, endIndex);

  return (
    <Box minH="100vh" bg="linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)">
      <Container maxW={isMobile ? "100%" : "6xl"} px={isMobile ? 4 : 6} py={8}>
        {/* Header */}
        <VStack align="start" spacing={6} mb={8}>
          <HStack justify="space-between" width="100%" flexDirection={isMobile ? "column" : "row"} align={isMobile ? "start" : "center"} spacing={isMobile ? 3 : 0}>
            <VStack align="start" spacing={2}>
              <Heading as="h1" size={isMobile ? "lg" : "2xl"} color="gray.900" fontWeight="700">
                Renest Cases
              </Heading>
              <Text fontSize={isMobile ? "sm" : "md"} color="gray.600">
                Manage in progress and archived cases
              </Text>
            </VStack>
            {userRole === 'admin' && (
              <Button
                leftIcon={<AddIcon />}
                bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                color="white"
                _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
                onClick={handleAddCase}
                size={isMobile ? "md" : "lg"}
                width={isMobile ? "full" : "auto"}
                fontWeight="600"
                borderRadius="lg"
              >
                New Case
              </Button>
            )}
          </HStack>
        </VStack>

        {/* Cases Display - Mobile or Desktop */}
        {isLoading ? (
          <Center py={12}>
            <VStack spacing={4}>
              <Spinner
                thickness="4px"
                speed="0.65s"
                emptyColor="gray.200"
                color="blue.800"
                size="lg"
              />
              <Text color="gray.700">Loading cases...</Text>
            </VStack>
          </Center>
        ) : cases.length === 0 ? (
          <Center py={12}>
            <VStack spacing={4}>
              <Text fontSize="lg" color="gray.800">
                No cases found
              </Text>
              <Button bg="blue.700" color="white" _hover={{ bg: "blue.800" }} onClick={handleAddCase}>
                Create your first case
              </Button>
            </VStack>
          </Center>
        ) : isMobile ? (
          // Mobile View - Card Layout
          <VStack spacing={4} align="stretch">
            {paginatedRows.map((row) => (
              <MobileCaseCard key={row.id} caseItem={row.original} />
            ))}
          </VStack>
        ) : (
          // Desktop View - Table Layout
          <Box
            bg="white"
            borderRadius="2xl"
            boxShadow="0 10px 30px rgba(0, 0, 0, 0.1)"
            overflowX="auto"
            border="1px solid"
            borderColor="gray.100"
          >
            <Table variant="simple">
              <Thead bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)">
                {table.getHeaderGroups().map((headerGroup) => (
                  <Tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <Th
                        key={header.id}
                        onClick={header.column.getToggleSortingHandler()}
                        cursor={header.column.getCanSort() ? 'pointer' : 'default'}
                        userSelect="none"
                        color="white"
                        fontWeight="700"
                        fontSize="sm"
                      >
                        <Flex align="center" gap={2}>
                          <Text>
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </Text>
                          {header.column.getCanSort() && (
                            <Box>
                              {header.column.getIsSorted() === 'asc' ? (
                                <ChevronUpIcon />
                              ) : header.column.getIsSorted() === 'desc' ? (
                                <ChevronDownIcon />
                              ) : null}
                            </Box>
                          )}
                        </Flex>
                      </Th>
                    ))}
                  </Tr>
                ))}
              </Thead>
              <Tbody>
                {paginatedRows.map((row) => (
                  <Tr key={row.id} _hover={{ bg: 'linear-gradient(90deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)' }} borderBottomColor="gray.100">
                    {row.getVisibleCells().map((cell) => (
                      <Td key={cell.id} py={4} color="gray.800" fontSize="sm">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Td>
                    ))}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}

        {/* Pagination Controls */}
        {cases.length > itemsPerPage && (
          <HStack justify="center" spacing={isMobile ? 2 : 4} mt={6} flexWrap="wrap">
            <Button
              onClick={handlePreviousPage}
              isDisabled={currentPage === 1}
              variant="outline"
              size={isMobile ? "sm" : "md"}
            >
              Previous
            </Button>
            
            <HStack spacing={1} display={isMobile ? "none" : "flex"}>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <Button
                  key={page}
                  onClick={() => goToPage(page)}
                  colorScheme={currentPage === page ? 'blue' : 'gray'}
                  variant={currentPage === page ? 'solid' : 'outline'}
                  size="sm"
                >
                  {page}
                </Button>
              ))}
            </HStack>

            {isMobile && (
              <Text fontSize="sm" color="gray.800">
                Page {currentPage} of {totalPages}
              </Text>
            )}

            <Button
              onClick={handleNextPage}
              isDisabled={currentPage === totalPages}
              variant="outline"
              size={isMobile ? "sm" : "md"}
            >
              Next
            </Button>
          </HStack>
        )}

        {/* Page Info */}
        {totalItems > 0 && (
          <Center mt={4}>
            <Text fontSize={isMobile ? "xs" : "sm"} color="gray.800">
              Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} cases
            </Text>
          </Center>
        )}
      </Container>

      {/* Case Details Modal */}
      <CaseDetailsModal
        isOpen={isDetailsOpen}
        onClose={onDetailsClose}
        selectedCase={selectedCase}
        onSaveSuccess={fetchCases}
        userRole={userRole}
        onEdit={() => {
          // TODO: Implement edit functionality
          console.log('Edit case:', selectedCase);
        }}
      />

      {/* New Case Modal */}
      <Modal isOpen={isOpen} onClose={handleModalClose} size={isMobile ? "full" : "2xl"} scrollBehavior="inside">
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent maxH="100vh" borderRadius="2xl" boxShadow="0 20px 60px rgba(0, 0, 0, 0.2)">
          <ModalHeader fontSize="2xl" fontWeight="bold" bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)" color="white" borderRadius="2xl 2xl 0 0">New Case</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={6} align="stretch">
              {/* Patient Info Section */}
              <Box>
                <Heading as="h3" size="md" color="blue.800" mb={4}>
                  Patient Info
                </Heading>
                <VStack spacing={3} align="stretch">
                  <FormControl isRequired isInvalid={!!validationErrors.title}>
                    <FormLabel fontSize="xs" color="gray.700" fontWeight="semibold">
                      SPECIES
                    </FormLabel>
                    <Select
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="-- Select Species --"
                      borderColor={validationErrors.title ? 'red.500' : undefined}
                    >
                      {speciesOptions}
                    </Select>
                    {validationErrors.title && (
                      <Text fontSize="sm" color="red.500" mt={1}>
                        {validationErrors.title}
                      </Text>
                    )}
                  </FormControl>

                  <HStack spacing={2} align="flex-end">
                    <FormControl>
                      <FormLabel fontSize="xs" color="gray.700" fontWeight="semibold">
                        WRMD PREFIX
                      </FormLabel>
                      <Select
                        name="wrmdPrefix"
                        value={formData.wrmdPrefix || ''}
                        onChange={handleInputChange}
                        placeholder="-- Select Prefix --"
                        size="sm"
                      >
                        <option value="PWSD">PWSD</option>
                        <option value="PWR">PWR</option>
                      </Select>
                    </FormControl>
                      <Text>{currentYear}-</Text>
                    <FormControl isRequired isInvalid={!!validationErrors.wrmd}>
                      <FormLabel fontSize="xs" color="gray.700" fontWeight="semibold">
                        WRMD CODE
                      </FormLabel>
                      <Input
                        name="wrmd"
                        type="number"
                        placeholder="e.g., 1455"
                        value={formData.wrmd || ''}
                        onChange={handleInputChange}
                        size="sm"
                        borderColor={validationErrors.wrmd ? 'red.500' : undefined}
                        min="0"
                      />
                      {validationErrors.wrmd && (
                        <Text fontSize="sm" color="red.500" mt={1}>
                          {validationErrors.wrmd}
                        </Text>
                      )}
                    </FormControl>
                  </HStack>

                  <FormControl>
                    <FormLabel fontSize="xs" color="gray.700" fontWeight="semibold">
                      DATE ADMITTED
                    </FormLabel>
                    <Input
                      type="date"
                      name="date"
                      value={formData.date || ''}
                      onChange={handleInputChange}
                      size="sm"
                    />
                  </FormControl>

                  <FormControl isRequired isInvalid={!!validationErrors.location}>
                    <FormLabel fontSize="xs" color="gray.700" fontWeight="semibold">
                      GENERAL REGION/CITY
                    </FormLabel>
                    <Input
                      name="location"
                      placeholder="Enter location"
                      value={formData.location}
                      onChange={handleInputChange}
                      size="sm"
                      autoComplete="new-password"
                      spellCheck="false"
                      borderColor={validationErrors.location ? 'red.500' : undefined}
                    />
                    {validationErrors.location && (
                      <Text fontSize="sm" color="red.500" mt={1}>
                        {validationErrors.location}
                      </Text>
                    )}
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="xs" color="gray.700" fontWeight="semibold">
                      POINT OF CONTACT (VOLUNTEER)
                    </FormLabel>
                    <Input
                      name="pointOfContact"
                      placeholder="Enter point of contact"
                      value={formData.pointOfContact || ''}
                      onChange={handleInputChange}
                      size="sm"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="xs" color="gray.700" fontWeight="semibold">
                      MOVEMENTS
                    </FormLabel>
                    <Select
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="-- Select Movement --"
                      size="sm"
                    >
                      <option value="Keep at Bahde">Keep at Bahde</option>
                      <option value="Keep at Ramona">Keep at Ramona</option>
                      <option value="Move through care">Move through care</option>
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="xs" color="gray.700" fontWeight="semibold">
                      STATUS
                    </FormLabel>
                    <Select
                      name="status"
                      value={formData.status || ''}
                      onChange={handleInputChange}
                      placeholder="-- Select Status --"
                      size="sm"
                    >
                      <option value="Candidate">Candidate</option>
                      <option value="Candidate: Wild Foster">Candidate: Wild Foster</option>
                      <option value="Investigation">Investigation</option>
                      <option value="R&R in Process">R&R in Process</option>
                      <option value="Renest Successful">Renest Successful</option>
                      <option value="Foster Successful">Foster Successful</option>
                      <option value="Renest/Foster Unsuccessful">Renest/Foster Unsuccessful</option>
                      <option value="Patient HE/DIC">Patient HE/DIC</option>
                      <option value="Not a Candidate">Not a Candidate</option>
                    </Select>
                  </FormControl>
                </VStack>
              </Box>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <HStack spacing={3}>
              <Button variant="outline" onClick={onClose} isDisabled={isLoadingForm} fontWeight="600">
                Cancel
              </Button>
              <Button
                bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                color="white"
                _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                onClick={handleSubmit}
                isLoading={isLoadingForm}
                fontWeight="600"
              >
                Create Case
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={undefined}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Case
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete case <strong>{caseToDelete?.id}</strong>? This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleConfirmDelete}
                ml={3}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default Cases;
